import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Plus, X, Calendar, ArrowLeft, ArrowDownUp, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { ADULT_RIG_DATA } from '@/components/booking/bookingDefaults';
import RigDetailEditor from '@/components/admin/RigDetailEditor';
import ReorderableList from '@/components/admin/ReorderableList';

export default function AdminRigs() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [showNew, setShowNew] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [reordering, setReordering] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const queryClient = useQueryClient();

  const { data: rigRecords = [], isLoading } = useQuery({
    queryKey: ['booking-rigs'],
    queryFn: () => base44.entities.BookingRig.list('sort_order'),
    enabled: currentUser?.role === 'admin',
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['booking-rigs'] });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const findRecord = (records, key) => records.find((r) => r.key === key);
  const customRigs = rigRecords.filter((r) => !ADULT_RIG_DATA[r.key]);

  // Unified, sort-ordered list of every rig (defaults + customs) for the reorder mode.
  const allRigItems = [
    ...Object.keys(ADULT_RIG_DATA).map((key) => {
      const rec = findRecord(rigRecords, key);
      return { key, isDefault: true, record: rec, name: rec?.name || ADULT_RIG_DATA[key].name, sort_order: rec?.sort_order ?? 0 };
    }),
    ...customRigs.map((rec) => ({ key: rec.key, isDefault: false, record: rec, name: rec.name, sort_order: rec.sort_order ?? 0 })),
  ].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  // Persist a new order: update sort_order on each record, creating a seeded
  // record (from the default) for any default rig that doesn't have one yet.
  const persistRigOrder = async (newOrder) => {
    setSavingOrder(true);
    try {
      await Promise.all(newOrder.map(async (it, i) => {
        if (it.record?.id) {
          await base44.entities.BookingRig.update(it.record.id, { sort_order: i });
        } else if (it.isDefault) {
          const d = ADULT_RIG_DATA[it.key];
          await base44.entities.BookingRig.create({
            key: it.key, name: d.name, tag: d.tag, description: d.description,
            specs: [], image_url: '', gallery_images: [], active: true, sort_order: i,
          });
        }
      }));
      invalidate();
    } catch (e) {
      toast({ title: 'Could not save order', description: e.message, variant: 'destructive' });
    } finally {
      setSavingOrder(false);
    }
  };

  const tabs = [
    ...Object.keys(ADULT_RIG_DATA).map((key) => ({
      key, label: ADULT_RIG_DATA[key].name, record: findRecord(rigRecords, key), defaultData: ADULT_RIG_DATA[key],
    })),
    ...customRigs.map((rec) => ({ key: 'custom_' + rec.id, label: rec.name, record: rec })),
  ];

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-10 md:py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Sim Rigs</h1>
          <p className="text-white/30 text-sm mt-2 max-w-xl">
            Select a rig to edit its booking details and full showcase page — specs, ratings, gallery and more. Changes go live instantly.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-12 max-w-5xl mx-auto">
        {/* Tab bar */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((t, i) => (
            <button key={t.key} onClick={() => { setActiveTab(i); setShowNew(false); }}
              className={`whitespace-nowrap px-4 py-2.5 text-xs font-bold tracking-wider border transition-colors ${!showNew && activeTab === i ? 'border-primary bg-primary text-white' : 'border-white/10 text-white/50 hover:text-white hover:border-white/30'}`}>
              {t.label}
            </button>
          ))}
          <button onClick={() => setShowNew(true)}
            className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold tracking-wider border transition-colors ${showNew ? 'border-primary bg-primary text-white' : 'border-dashed border-white/15 text-white/50 hover:text-white hover:border-white/30'}`}>
            <Plus className="w-3.5 h-3.5" /> NEW RIG
          </button>
          <button onClick={() => { setReordering((r) => !r); setShowNew(false); }}
            className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold tracking-wider border transition-colors ${reordering ? 'border-primary bg-primary text-white' : 'border-white/15 text-white/50 hover:text-white hover:border-white/30'}`}>
            {reordering ? <><Check className="w-3.5 h-3.5" /> DONE</> : <><ArrowDownUp className="w-3.5 h-3.5" /> REORDER</>}
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : reordering ? (
          <div>
            <p className="text-white/40 text-xs mb-3">Drag to reorder the rigs shown in the booking rig picker. Sorting is saved automatically as you drag.</p>
            <ReorderableList
              items={allRigItems}
              onReorder={persistRigOrder}
              getLabel={(it) => it.name}
              getBadge={(it) => <span className="text-[10px] font-bold tracking-widest px-2 py-1 shrink-0 bg-white/5 text-white/30">{it.isDefault ? 'DEFAULT' : 'CUSTOM'}</span>}
            />
            {savingOrder && <div className="flex justify-center mt-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>}
          </div>
        ) : showNew ? (
          <div className="relative">
            <button onClick={() => setShowNew(false)} className="absolute -top-2 right-0 text-white/30 hover:text-white z-10" aria-label="Cancel">
              <X className="w-4 h-4" />
            </button>
            <RigDetailEditor isNew onCreated={() => { setShowNew(false); invalidate(); }} />
          </div>
        ) : tabs[activeTab] ? (
          <RigDetailEditor
            key={tabs[activeTab].key}
            rigKey={tabs[activeTab].key}
            defaultData={tabs[activeTab].defaultData}
            record={tabs[activeTab].record}
            onSaved={invalidate}
            onDelete={invalidate}
          />
        ) : null}
      </section>

      <div className="px-6 lg:px-10 pb-12 max-w-5xl mx-auto">
        <Link to="/bookings" className="inline-flex items-center gap-2 text-primary text-xs font-bold tracking-wider hover:underline">
          <Calendar className="w-4 h-4" /> VIEW BOOKINGS PAGE
        </Link>
      </div>
    </div>
  );
}